// src/pages/AddTurno.tsx
import React, { useState, ChangeEvent, FormEvent } from "react";
import {
  TextField,
  Button,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  InputAdornment,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { SelectChangeEvent } from "@mui/material/Select";

// Componente TimeSelect: Permite seleccionar hora y minutos (sólo "00" y "30") o dejar en blanco
interface TimeSelectProps {
  value: string; // formato "HH:mm" o vacío
  onChange: (value: string) => void;
}
const TimeSelect: React.FC<TimeSelectProps> = ({ value, onChange }) => {
  const [hour, setHour] = useState<string>("");
  const [minute, setMinute] = useState<string>("");

  React.useEffect(() => {
    if (value) {
      const parts = value.split(":");
      if (parts.length === 2) {
        setHour(parts[0]);
        setMinute(parts[1]);
      }
    } else {
      setHour("");
      setMinute("");
    }
  }, [value]);

  const hourOptions = Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, "0")
  );
  const minuteOptions = ["00", "30"];

  const handleHourChange = (e: SelectChangeEvent<string>) => {
    const newHour = e.target.value;
    setHour(newHour);
    if (newHour && minute) {
      onChange(`${newHour}:${minute}`);
    } else {
      onChange("");
    }
  };

  const handleMinuteChange = (e: SelectChangeEvent<string>) => {
    const newMinute = e.target.value;
    setMinute(newMinute);
    if (hour && newMinute) {
      onChange(`${hour}:${newMinute}`);
    } else {
      onChange("");
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 4 }}>
      <FormControl sx={{ minWidth: 80 }}>
        <InputLabel id="hour-select-label">Hora</InputLabel>
        <Select
          labelId="hour-select-label"
          label="Hora"
          value={hour}
          onChange={handleHourChange}
          required
        >
          <MenuItem value="">Ninguno</MenuItem>
          {hourOptions.map((hr) => (
            <MenuItem key={hr} value={hr}>
              {hr}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl sx={{ minWidth: 80 }}>
        <InputLabel id="minute-select-label">Minutos</InputLabel>
        <Select
          labelId="minute-select-label"
          label="Minutos"
          value={minute}
          onChange={handleMinuteChange}
          required
        >
          <MenuItem value="">Ninguno</MenuItem>
          {minuteOptions.map((min) => (
            <MenuItem key={min} value={min}>
              {min}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

interface TurnoForm {
  mesInicio: string;
  diaSemana: string;
  hora: string;
  cancha: string;
  titular: string;
  telefono: string;
  dni: string;
  precio: number | "";
  precioAdelantado: number | "";
  metodoPago: string[]; // Usaremos checkboxes para cada opción
}

const diasSemana = [
  { value: "lunes", label: "Lunes" },
  { value: "martes", label: "Martes" },
  { value: "miercoles", label: "Miércoles" },
  { value: "jueves", label: "Jueves" },
  { value: "viernes", label: "Viernes" },
  { value: "sabado", label: "Sábado" },
  { value: "domingo", label: "Domingo" },
];

// Función para generar opciones para el mes de inicio
const getMesInicioOptions = () => {
  const today = new Date();
  const nextMonthDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const daysUntilNextMonth =
    (nextMonthDate.getTime() - today.getTime()) / (1000 * 3600 * 24);
  const monthAfterNextDate = new Date(
    today.getFullYear(),
    today.getMonth() + 2,
    1
  );
  const monthsData = [
    { value: "1", label: "Enero" },
    { value: "2", label: "Febrero" },
    { value: "3", label: "Marzo" },
    { value: "4", label: "Abril" },
    { value: "5", label: "Mayo" },
    { value: "6", label: "Junio" },
    { value: "7", label: "Julio" },
    { value: "8", label: "Agosto" },
    { value: "9", label: "Septiembre" },
    { value: "10", label: "Octubre" },
    { value: "11", label: "Noviembre" },
    { value: "12", label: "Diciembre" },
  ];
  const options: { value: string; label: string }[] = [];
  // Incluir mes siguiente solo si faltan más de 5 días para que inicie
  if (daysUntilNextMonth > 5) {
    const nextMonthValue = (nextMonthDate.getMonth() + 1).toString();
    const nextMonthLabel =
      monthsData.find((m) => m.value === nextMonthValue)?.label || "";
    options.push({ value: nextMonthValue, label: nextMonthLabel });
  }
  // Incluir mes siguiente del siguiente siempre
  const monthAfterNextValue = (monthAfterNextDate.getMonth() + 1).toString();
  const monthAfterNextLabel =
    monthsData.find((m) => m.value === monthAfterNextValue)?.label || "";
  options.push({ value: monthAfterNextValue, label: monthAfterNextLabel });
  return options;
};

const canchas = ["Cancha 1", "Cancha 2", "Cancha 3"];

const AddTurno: React.FC = () => {
  const navigate = useNavigate();
  const mesInicioOptions = getMesInicioOptions();
  const defaultMesInicio =
    mesInicioOptions.length > 0 ? mesInicioOptions[0].value : "";

  const [formData, setFormData] = useState<TurnoForm>({
    mesInicio: defaultMesInicio,
    diaSemana: "",
    hora: "",
    cancha: "",
    titular: "",
    telefono: "",
    dni: "",
    precio: "",
    precioAdelantado: "",
    metodoPago: ["debito"], // Por defecto, seleccionado "Débito/Transferencia"
  });

  // Manejo para campos de texto
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const target = e.target as HTMLInputElement;
    const { name, value } = target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Manejo para selects
  const handleSelectChange = (e: SelectChangeEvent<string | string[]>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Manejo para checkboxes de métodos de pago
  const handleCheckboxChange =
    (value: string) => (e: ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.target.checked;
      setFormData((prev) => {
        const current = prev.metodoPago;
        let updated: string[];
        if (isChecked) {
          updated = current.includes(value) ? current : [...current, value];
        } else {
          updated = current.filter((item) => item !== value);
        }
        return { ...prev, metodoPago: updated };
      });
    };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    // Validar campos obligatorios:
    // Obligatorios: mesInicio, diaSemana, hora, cancha, titular, telefono, precio, y al menos un método de pago.
    if (
      !formData.mesInicio ||
      !formData.diaSemana ||
      !formData.hora ||
      !formData.cancha ||
      !formData.titular ||
      !formData.telefono ||
      formData.precio === "" ||
      formData.metodoPago.length === 0
    ) {
      alert("Por favor, complete todos los campos obligatorios.");
      return;
    }
    console.log("Turno agregado:", formData);
    alert("Turno agregado (demo)");
    navigate("/");
  };

  return (
    <Paper style={{ padding: "20px", maxWidth: "600px", margin: "auto" }}>
      <Typography variant="h5" sx={{ mb: 8 }}>
        Agregar Nuevo Turno Fijo
      </Typography>
      <form onSubmit={handleSubmit}>
        {/* Sección Turno */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom>
            Turno
          </Typography>
          <Divider sx={{ mb: 4 }} />
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="mesInicio-label">Mes de inicio</InputLabel>
            <Select
              labelId="mesInicio-label"
              label="Mes de inicio"
              name="mesInicio"
              value={formData.mesInicio}
              onChange={handleSelectChange}
            >
              {mesInicioOptions.map((mes) => (
                <MenuItem key={mes.value} value={mes.value}>
                  {mes.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="diaSemana-label">Día de la semana</InputLabel>
            <Select
              labelId="diaSemana-label"
              label="Día de la semana"
              name="diaSemana"
              value={formData.diaSemana}
              onChange={handleSelectChange}
            >
              {diasSemana.map((dia) => (
                <MenuItem key={dia.value} value={dia.value}>
                  {dia.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ my: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Seleccione la hora
            </Typography>
            <TimeSelect
              value={formData.hora}
              onChange={(newTime) =>
                setFormData((prev) => ({ ...prev, hora: newTime }))
              }
            />
          </Box>
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="cancha-label">Cancha</InputLabel>
            <Select
              labelId="cancha-label"
              label="Cancha"
              name="cancha"
              value={formData.cancha}
              onChange={handleSelectChange}
            >
              {canchas.map((cancha) => (
                <MenuItem key={cancha} value={cancha}>
                  {cancha}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Sección Titular */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom>
            Titular
          </Typography>
          <Divider sx={{ mb: 4 }} />
          <TextField
            label="Nombre del Titular"
            name="titular"
            fullWidth
            margin="normal"
            value={formData.titular}
            onChange={handleChange}
            required
          />
          <TextField
            label="Teléfono"
            name="telefono"
            fullWidth
            margin="normal"
            value={formData.telefono}
            onChange={handleChange}
            required
          />
          <TextField
            label="DNI (opcional)"
            name="dni"
            fullWidth
            margin="normal"
            value={formData.dni}
            onChange={handleChange}
          />
        </Box>

        {/* Sección Precio */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom>
            Precio
          </Typography>
          <Divider sx={{ mb: 4 }} />
          <TextField
            label="Precio"
            name="precio"
            type="number"
            fullWidth
            margin="normal"
            value={formData.precio}
            onChange={handleChange}
            required
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              },
            }}
          />
          <TextField
            label="Precio por pago adelantado (opcional)"
            name="precioAdelantado"
            type="number"
            fullWidth
            margin="normal"
            value={formData.precioAdelantado}
            onChange={handleChange}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              },
            }}
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Métodos de pago
            </Typography>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.metodoPago.includes("efectivo")}
                    onChange={handleCheckboxChange("efectivo")}
                    color="primary"
                  />
                }
                label="Efectivo"
                sx={{
                  color: formData.metodoPago.includes("efectivo")
                    ? "primary.main"
                    : "text.primary",
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.metodoPago.includes("debito")}
                    onChange={handleCheckboxChange("debito")}
                    color="primary"
                  />
                }
                label="Débito/Transferencia"
                sx={{
                  color: formData.metodoPago.includes("debito")
                    ? "primary.main"
                    : "text.primary",
                }}
              />
            </FormGroup>
          </Box>
        </Box>

        <Button
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          sx={{ mt: 2 }}
        >
          Agregar Turno
        </Button>
      </form>
    </Paper>
  );
};

export default AddTurno;
